import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { APIClientCreateOrRotateResponse } from '../../../hooks/api-client-hooks';

type Props = {
  open: boolean;
  result: APIClientCreateOrRotateResponse | null;
  onClose: () => void;
};

const APIClientSecretResultDialog: React.FC<Props> = ({
                                                        open,
                                                        result,
                                                        onClose,
                                                      }) => {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  const secret = result?.client_secret || '';

  const copySecret = async () => {
    if (!secret) return;

    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>API Client Secret</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning">
            This client secret is returned only once. Copy it now and store it securely.
          </Alert>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Client ID
            </Typography>

            <Typography
              variant="body2"
              fontFamily="monospace"
              sx={{ wordBreak: 'break-all' }}
            >
              {result?.client_id || '-'}
            </Typography>
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Client Secret"
            value={secret}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? 'Copied' : 'Copy secret'}>
                    <span>
                      <IconButton onClick={copySecret} disabled={!secret}>
                        <ContentCopyIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          {copied ? (
            <Alert severity="success">
              Secret copied to clipboard.
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          I have copied the secret
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default APIClientSecretResultDialog;